import { f7, useStore } from 'framework7-react';
import { FC, useEffect, useState } from 'react';
import { fetchMAffiliateSettingsApi } from '@/api/infoApi';
import { closePaidyApi, registerPaidyTokenApi } from '@/api/payment';
import { postCreateGmoLinkTypePlusCardParameterApi } from '@/api/prepaidApi';
import {
  getSubscriptionStatusApi,
  postSubscriptionInitialPaymentApi,
  postSubscriptionActivateApi,
  postSubscriptionRegisterVehicleApi,
} from '@/api/subscription';
import { PageTemplate } from '@/components/uiParts/Template/PageTemplate/PageTemplate';
import { isApp } from '@/config/device';
import { initialState } from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { endpoint } from '@/consts/api';
import { paymentTypeId } from '@/consts/payment';
import { getAndSetCustomerInfo } from '@/hooks/api/customer/useCustomerInfo';
import { useGmo } from '@/hooks/useGmo';
import { useGmoRegisterAndEditRedirect } from '@/hooks/useGmoRegisterAndEditRedirect';
import { useLoadCustomer } from '@/hooks/useLoadCustomer';
import { usePaidy } from '@/hooks/usePaidy';
import { SubscriptionStatus } from '@/types/api/subscription';
import { PAIDY_LOGO_URL } from '@/utils/payment';
import { storeDispatch } from '@/utils/store';

interface SubscriptionPaymentPageProps {
  subscriptionId?: number;
  planName?: string;
  monthlyFee?: number;
  status?: string;
  tStockCarId?: number;
}

// 決済の進行ステップ
type PaymentStep = 'select' | 'initial_done' | 'all_done';

export const SubscriptionPaymentPage: FC<SubscriptionPaymentPageProps> = ({
  subscriptionId: propSubscriptionId,
  planName: propPlanName,
  monthlyFee: propMonthlyFee,
  status,
  tStockCarId: propTStockCarId,
}) => {
  // tStockCarId を localStorage で永続化（GMOリダイレクト後に復元するため）
  // 顧客・契約スコープ付き JSON { cid, sid, tStockCarId } で保存し、
  // 別ユーザー/別契約の残骸による誤紐付けを防ぐ
  // props 由来の値のみ即座に state に入れ、localStorage 復元は検証 useEffect に委ねる
  const [tStockCarId, setTStockCarId] = useState<number | undefined>(
    propTStockCarId || undefined,
  );
  // 既存車フローかどうか（props または localStorage 復元で確定、リダイレクト後も維持）
  const [isExistingCarFlow, setIsExistingCarFlow] = useState(!!propTStockCarId);

  // propTStockCarId を localStorage に保存（副作用は useEffect で実行）
  useEffect(() => {
    if (!propTStockCarId) return;
    const cid =
      (store.state.authInfo as any)?.m_customer_id ||
      Number(window.localStorage.getItem('m_customer_id') || '0');
    window.localStorage.setItem(
      'subscription_t_stock_car_id',
      JSON.stringify({
        cid,
        sid: propSubscriptionId || 0,
        tStockCarId: propTStockCarId,
      }),
    );
  }, [propTStockCarId, propSubscriptionId]);

  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStep, setPaymentStep] = useState<PaymentStep>('select');
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(
    null,
  );
  const [statusFetched, setStatusFetched] = useState(false);
  const authInfo = store.state.authInfo as any;

  const subscriptionId =
    propSubscriptionId || (subscription as any)?.t_subscription_id;

  // inflight: {ts: number, cid: number} を JSON で保存。現在の顧客と一致する場合のみ有効
  const parseInflight = (): { ts: number; cid: number } | null => {
    try {
      const raw = window.localStorage.getItem('subscription_payment_inflight');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (
        parsed &&
        typeof parsed.ts === 'number' &&
        typeof parsed.cid === 'number'
      ) {
        return parsed;
      }
      // 旧形式（タイムスタンプのみ）→ 破棄（顧客スコープ不明のため無効）
      window.localStorage.removeItem('subscription_payment_inflight');
      return null;
    } catch {
      window.localStorage.removeItem('subscription_payment_inflight');
      return null;
    }
  };

  // 初期値は false/0。authInfo 復元後に useEffect で再評価
  const [isInflight, setIsInflight] = useState(false);
  const [inflightTs, setInflightTs] = useState(0);

  // authInfo.m_customer_id が確定したら inflight を再評価
  useEffect(() => {
    const cid = authInfo?.m_customer_id;
    if (!cid) return;
    const inflight = parseInflight();
    if (!inflight) {
      if (isInflight) setIsInflight(false);
      return;
    }
    if (inflight.cid === cid) {
      setIsInflight(true);
      setInflightTs(inflight.ts);
    } else {
      // 別ユーザーの残骸 → 破棄
      window.localStorage.removeItem('subscription_payment_inflight');
      if (isInflight) setIsInflight(false);
    }
  }, [authInfo?.m_customer_id]);

  // authInfo.m_customer_id / subscriptionId 確定後に tStockCarId のスコープを検証
  useEffect(() => {
    const cid = authInfo?.m_customer_id;
    if (!cid) return;
    try {
      const raw = window.localStorage.getItem('subscription_t_stock_car_id');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.tStockCarId !== 'number') {
        window.localStorage.removeItem('subscription_t_stock_car_id');
        setTStockCarId(undefined);
        return;
      }
      if (parsed.cid && parsed.cid !== cid) {
        // props から渡された値であれば正しい cid/sid で再保存
        if (propTStockCarId && parsed.tStockCarId === propTStockCarId) {
          window.localStorage.setItem(
            'subscription_t_stock_car_id',
            JSON.stringify({
              cid,
              sid: subscriptionId || parsed.sid || 0,
              tStockCarId: parsed.tStockCarId,
            }),
          );
          return;
        }
        // 別顧客の残骸 → 破棄
        window.localStorage.removeItem('subscription_t_stock_car_id');
        setTStockCarId(undefined);
        return;
      }
      if (parsed.sid && subscriptionId && parsed.sid !== subscriptionId) {
        // 別契約の残骸 → 破棄
        window.localStorage.removeItem('subscription_t_stock_car_id');
        setTStockCarId(undefined);
        return;
      }
      // sid が保存されているが subscriptionId 未確定 → sid 検証不能なので待機
      // （ここで state に入れると同一 flush で auto-link が未検証の値で発火しうる）
      if (parsed.sid && parsed.sid !== 0 && !subscriptionId) {
        return;
      }
      // 検証通過 → state に反映（localStorage 復元値はここで初めて state に入る）
      setTStockCarId(parsed.tStockCarId);
      setIsExistingCarFlow(true);
      // cid/sid が未設定 → 正しい値で補完
      const needsUpdate =
        !parsed.cid ||
        parsed.cid === 0 ||
        (subscriptionId && (!parsed.sid || parsed.sid === 0));
      if (needsUpdate) {
        window.localStorage.setItem(
          'subscription_t_stock_car_id',
          JSON.stringify({
            cid,
            sid: subscriptionId || parsed.sid || 0,
            tStockCarId: parsed.tStockCarId,
          }),
        );
      }
    } catch {
      window.localStorage.removeItem('subscription_t_stock_car_id');
      setTStockCarId(undefined);
    }
  }, [authInfo?.m_customer_id, subscriptionId]);

  const paymentMethods = useStore(store, 'getPaymentMethodsInfo') as any[];
  const { launchPaidyCheckout } = usePaidy();
  const { addCreditCardPaymentMethod } = useGmo(paths.subscriptionPayment);
  const { handleGmoRegisterAndEditRedirect } = useGmoRegisterAndEditRedirect();

  // GMOカード登録からの戻り: useLoadCustomerでオートログイン
  // statusがない場合（通常SPA遷移）はautoLoginを発動させない
  // （localStorageの古いトークンでstoreを上書きしてしまうため）
  const customerId = status
    ? window.localStorage.getItem('m_customer_id')
    : null;
  const token = status
    ? window.localStorage.getItem('token') ?? undefined
    : undefined;
  const currentStatus = status;
  const { isLoggedIn } = useLoadCustomer(
    f7.views.main.router,
    token,
    Number(customerId),
    currentStatus,
  );

  // GMOセッション再利用検出（同期）
  const isSuccessStatus = status?.includes('success');
  const isCancelStatus = status?.includes('cancel');
  const [isGmoRedirecting] = useState(() => {
    if (!status) return false;
    const expectedReturn = window.localStorage.getItem('gmo_expected_return');
    const expectedCancel = window.localStorage.getItem('gmo_expected_cancel');
    if (
      isSuccessStatus &&
      expectedReturn &&
      !expectedReturn.includes('/subscription/payment/')
    )
      return true;
    if (
      isCancelStatus &&
      expectedCancel &&
      !expectedCancel.includes('/subscription/payment/')
    )
      return true;
    return false;
  });

  // GMOセッション再利用: 間違ったページに来た場合リダイレクト
  useEffect(() => {
    if (!status) return;
    const expectedReturn = window.localStorage.getItem('gmo_expected_return');
    const expectedCancel = window.localStorage.getItem('gmo_expected_cancel');

    const redirectToCorrectPage = (targetUrl: string) => {
      const fromPage = window.localStorage.getItem('fromPage');
      if (fromPage) {
        window.localStorage.setItem('gmo_redirect_fromPage', fromPage);
      }
      window.localStorage.removeItem('gmo_expected_return');
      window.localStorage.removeItem('gmo_expected_cancel');
      window.location.replace(targetUrl);
    };

    if (
      isSuccessStatus &&
      expectedReturn &&
      !expectedReturn.includes('/subscription/payment/')
    ) {
      redirectToCorrectPage(expectedReturn);
      return;
    }
    if (
      isCancelStatus &&
      expectedCancel &&
      !expectedCancel.includes('/subscription/payment/')
    ) {
      redirectToCorrectPage(expectedCancel);
      return;
    }
    window.localStorage.removeItem('gmo_expected_return');
    window.localStorage.removeItem('gmo_expected_cancel');
  }, []);

  // inflightフラグ管理（v2設計: このキーのみ。type_id/method_id/success_reachedは廃止）
  // クリア条件: apply_status=0/2確定、payment_cancel、5分超過（再確認後）、handleSelectCard catch
  const clearInflightStorage = () => {
    window.localStorage.removeItem('subscription_payment_inflight');
    setIsInflight(false);
    setInflightTs(0);
  };

  // payment_success/cancel 即時処理（isLoggedIn を待たずに実行）
  useEffect(() => {
    if (currentStatus === 'payment_success') {
      const now = Date.now();
      const cid = Number(window.localStorage.getItem('m_customer_id') || '0');
      window.localStorage.setItem(
        'subscription_payment_inflight',
        JSON.stringify({ ts: now, cid }),
      );
      setInflightTs(now);
      if (!isInflight) setIsInflight(true);
    }
    if (currentStatus === 'payment_cancel') {
      clearInflightStorage();
    }
  }, [currentStatus]);

  // GMO戻り後の処理（顧客情報再取得 + URLクリーンアップ）
  useEffect(() => {
    if (!isLoggedIn || !currentStatus) return;

    const mCustomerId = window.localStorage.getItem('m_customer_id');

    if (currentStatus === 'gmo_success') {
      handleGmoRegisterAndEditRedirect(currentStatus, isLoggedIn);
    } else {
      if (mCustomerId) {
        getAndSetCustomerInfo({ m_customer_id: Number(mCustomerId) });
      }
      if (currentStatus === 'gmo_cancel') {
        handleGmoRegisterAndEditRedirect(currentStatus, isLoggedIn);
      }
    }

    // payment_cancel/success の inflight 処理は即時 useEffect（下記）で実行済み

    // localStorage クリーンアップ
    window.localStorage.removeItem('postGmoNav');
    window.localStorage.removeItem('destination_page');
    window.localStorage.removeItem('destination_step');
    window.localStorage.removeItem('fromPage');
    window.localStorage.removeItem('gmo_redirect_fromPage');

    // URLクリーンアップ + F7履歴永続化
    const cleanUrl = paths.subscriptionPayment;
    window.history.replaceState(null, '', cleanUrl);
    const history = f7.view.main.router.history;
    const idx = history.findIndex(
      (url: string) =>
        url.includes('/subscription/payment/gmo_') ||
        url.includes('/subscription/payment/payment_'),
    );
    if (idx !== -1) {
      history[idx] = cleanUrl;
    }
    window.localStorage.setItem(
      'f7router-view_main-history',
      JSON.stringify(history),
    );
  }, [isLoggedIn, currentStatus]);

  const getPaymentType = (pm: any) =>
    Number(pm.paymentTypeId || pm.m_payment_type_id || pm.payment_type_id || 0);
  const registeredCards = (paymentMethods || []).filter(
    (pm: any) => getPaymentType(pm) === paymentTypeId.CREDIT,
  );
  const allPaidys = (paymentMethods || []).filter(
    (pm: any) => getPaymentType(pm) === paymentTypeId.PAIDY,
  );
  const registeredPaidys =
    allPaidys.length > 0 ? [allPaidys[allPaidys.length - 1]] : [];

  // fetchStatus: apply_statusに応じてpaymentStepを決定（1回取得のみ）
  useEffect(() => {
    if (!authInfo?.m_customer_id) return;
    // payment_success到着直後、GMO戻りuseEffectでsetIsInflight(true)が反映されるまで待つ
    if (currentStatus === 'payment_success' && !isInflight) return;
    let isActive = true;

    const fetchStatus = async () => {
      try {
        const res = await getSubscriptionStatusApi(authInfo.m_customer_id);
        if (!isActive) return;
        const sub = res.data?.data?.subscription;
        if (!sub) return;

        setSubscription(sub);

        if (sub.apply_status === 0) {
          clearInflightStorage();
          setPaymentStep('all_done');
        } else if (sub.apply_status === 2) {
          clearInflightStorage();
          if (currentStatus !== 'payment_cancel') {
            setPaymentStep('initial_done');
          }
        }
        // apply_status=1: 何もしない
        // inflightならポーリングuseEffectがリトライ、なければStep1
      } catch (e) {
        console.error('契約状態取得エラー', e);
      } finally {
        if (isActive) setStatusFetched(true);
      }
    };

    fetchStatus();

    return () => {
      isActive = false;
    };
  }, [authInfo?.m_customer_id, currentStatus, isInflight]);

  // ポーリング: inflight中かつStep1（ローディング表示中）のとき、3秒間隔でapply_status確認
  // setTimeout再帰で多重実行を防止（API応答待ち中は次のリクエストを送らない）
  useEffect(() => {
    if (!isInflight || paymentStep !== 'select' || !authInfo?.m_customer_id)
      return;

    let timerId: ReturnType<typeof setTimeout>;
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await getSubscriptionStatusApi(authInfo.m_customer_id);
        if (cancelled) return;
        const sub = res.data?.data?.subscription;
        if (!sub) {
          timerId = setTimeout(poll, 3000);
          return;
        }

        setSubscription(sub);

        if (sub.apply_status === 0) {
          clearInflightStorage();
          setPaymentStep('all_done');
          return;
        }
        if (sub.apply_status === 2) {
          clearInflightStorage();
          setPaymentStep('initial_done');
          return;
        }
      } catch {
        // 取得失敗
      }
      if (!cancelled) {
        timerId = setTimeout(poll, 3000);
      }
    };

    timerId = setTimeout(poll, 3000);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [isInflight, paymentStep, authInfo?.m_customer_id]);

  // inflight期限切れタイマー: 5分後に最新状態を再確認し、未確定ならclearInflight
  // inflightTs に依存し、payment_success でタイムスタンプ更新時に再スケジュールされる
  useEffect(() => {
    if (!isInflight || inflightTs <= 0) return;

    const remainMs = Math.max(0, 5 * 60 * 1000 - (Date.now() - inflightTs));
    const timerId = setTimeout(async () => {
      const cid = authInfo?.m_customer_id;
      if (!cid) {
        // cid未取得 → 再ログイン導線へ
        clearInflightStorage();
        f7.dialog.alert(
          'ログイン情報を取得できませんでした。再度ログインしてください。',
          '',
          () => f7.view.main.router.navigate(paths.home),
        );
        return;
      }
      let shouldOfferRetry = false;
      try {
        const res = await getSubscriptionStatusApi(cid);
        const sub = res.data?.data?.subscription;
        if (sub?.apply_status === 0) {
          setSubscription(sub);
          clearInflightStorage();
          setPaymentStep('all_done');
          return;
        }
        if (sub?.apply_status === 2) {
          setSubscription(sub);
          clearInflightStorage();
          setPaymentStep('initial_done');
          return;
        }
        if (sub?.apply_status === 1) {
          shouldOfferRetry = true;
        } else {
          // sub=null or 想定外のapply_status → タイマー再スケジュール
          setInflightTs(Date.now());
          return;
        }
      } catch {
        // API失敗 → 状態不明なのでinflight維持、タイマー再スケジュール
        setInflightTs(Date.now());
        return;
      }
      if (!shouldOfferRetry) return;
      // apply_status=1: 5分経過でWebhook未到着 → GMO決済未完了と判断
      // GMO Webhookは通常数秒で到着する。5分後もapply_status=1なら
      // ユーザーがGMO画面到達前にアプリを落としたケースが大半。
      // processInitialPaymentは冪等なので、万一の再決済でも安全。
      f7.dialog.confirm(
        '決済が完了していない可能性があります。\nお支払い方法の選択からやり直しますか？',
        '',
        () => {
          // OK: inflight解除してStep1に戻す
          clearInflightStorage();
        },
        () => {
          // キャンセル: inflight維持、タイマー再スケジュール
          setInflightTs(Date.now());
        },
      );
    }, remainMs + 1000);

    return () => clearTimeout(timerId);
  }, [isInflight, inflightTs, authInfo?.m_customer_id]);

  const displayFee =
    propMonthlyFee || subscription?.monthly_billing_amount || 0;
  const displayPlanName = propPlanName || subscription?.plan_name || '';

  // 初回決済 API の通信例外時の補償処理
  // backend は capture と apply_status=2 更新を同一トランザクションで実行するため、
  // apply_status から処理確定状態を一意に判定できる。
  //   - 0: activate まで完了（Webhook 等） → all_done
  //   - 2: capture 成功・DB 更新済 → initial_done で activate ステップへ
  //   - 1: capture 未実行確定 → close で authorize 残留を回避し select に戻す
  //   - 不明（status null / API 失敗） → 二重課金防止で initial_done 維持
  const reconcileAfterInitialPaymentFailure = async (
    paidyPaymentId: string | null,
  ) => {
    try {
      const statusRes = await getSubscriptionStatusApi(
        Number(authInfo.m_customer_id),
      );
      const apply = statusRes?.data?.data?.subscription?.apply_status;
      if (apply === 0) {
        setPaymentStep('all_done');
        return;
      }
      if (apply === 2) {
        setPaymentStep('initial_done');
        return;
      }
      if (apply === 1 && paidyPaymentId) {
        // capture 未実行が確定 → authorize を解放して再試行可能に戻す
        await closePaidyApi({ payment_id: paidyPaymentId }).catch(() => {});
        setPaymentStep('select');
        return;
      }
    } catch {
      // status 取得失敗
    }
    // 状態不明: Checkout 再起動による二重課金を防ぐため initial_done に進める
    setPaymentStep('initial_done');
  };

  // 有効化 API の通信例外時の補償処理
  // capture は既に成功している前提のため、close は呼ばない（既決済を潰さない）。
  // 成功確定（apply_status=0）時は true を返し、呼び出し元で例外伝播を抑制する。
  const reconcileAfterActivateFailure = async (): Promise<boolean> => {
    try {
      const statusRes = await getSubscriptionStatusApi(
        Number(authInfo.m_customer_id),
      );
      const apply = statusRes?.data?.data?.subscription?.apply_status;
      if (apply === 0) {
        setPaymentStep('all_done');
        return true;
      }
    } catch {
      // status 取得失敗は initial_done 維持で許容
    }
    setPaymentStep('initial_done');
    return false;
  };

  // 登録済みPaidy: 都度決済→既存トークンで即有効化（token登録スキップ）
  const handleRegisteredPaidy = async (pm: any) => {
    if (isProcessing) return;
    setIsProcessing(true);

    let paidyPaymentId: string | null = null;
    try {
      const methodId = pm.m_payment_method_id || pm.paymentMethodId;

      // Step1: Paidy Checkout（payment mode）で初回都度決済
      const paymentResult = await launchPaidyCheckout({
        mode: 'payment',
        amount: displayFee,
        orderRef: `sub_initial_${authInfo.m_customer_id}_${Date.now()}`,
        storeName: displayPlanName,
      });

      if (!paymentResult?.paymentId) {
        setIsProcessing(false);
        return;
      }
      paidyPaymentId = paymentResult.paymentId;

      // apply_status 1→2 に更新
      // 通信例外: reconcile が apply_status から確定状態を判定し、適切な
      //          paymentStep に遷移させた上で例外伝播
      // 明示失敗(success=false): authorize 残留防止のため close で補償
      let res1;
      try {
        res1 = await postSubscriptionInitialPaymentApi({
          t_subscription_id: subscriptionId,
          m_payment_type_id: paymentTypeId.PAIDY,
          m_payment_method_id: methodId,
          paidy_payment_id: paidyPaymentId,
        });
      } catch (apiErr) {
        await reconcileAfterInitialPaymentFailure(paidyPaymentId);
        throw apiErr;
      }
      if (!res1.data.success) {
        await closePaidyApi({ payment_id: paidyPaymentId }).catch(() => {});
        throw new Error(
          res1.data.error_message || '初回決済の記録に失敗しました',
        );
      }

      // Step2: 既存トークンで即有効化（apply_status 2→0）
      // ここまでで初回決済(capture)は完了済みのため、activate 失敗時は
      // 'initial_done' に進めて再試行を activate のみに限定する。
      // 'select' のままだと再試行で Paidy Checkout が再起動されて二重課金になる。
      let res2;
      try {
        res2 = await postSubscriptionActivateApi({
          t_subscription_id: subscriptionId,
          m_payment_type_id: paymentTypeId.PAIDY,
          m_payment_method_id: methodId,
        });
      } catch (apiErr) {
        // activate 通信例外: capture は済んでいるので close は呼ばず
        // 成功確定（apply_status=0）なら例外伝播を抑制してダイアログ表示を防ぐ
        if (await reconcileAfterActivateFailure()) {
          return;
        }
        throw apiErr;
      }
      if (!res2.data.success) {
        setPaymentStep('initial_done');
        throw new Error(res2.data.error_message || '有効化に失敗しました');
      }

      setPaymentStep('all_done');
    } catch (e: any) {
      f7.dialog.alert(e.message || 'エラーが発生しました');
    } finally {
      setIsProcessing(false);
    }
  };

  // 新規Paidyで初回都度決済
  const handleSelectPaidy = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    let paidyPaymentId: string | null = null;
    try {
      // Step1: 初回都度決済（mode=payment）
      const paymentResult = await launchPaidyCheckout({
        mode: 'payment',
        amount: displayFee,
        orderRef: `sub_initial_${authInfo.m_customer_id}_${Date.now()}`,
        storeName: displayPlanName,
      });

      if (!paymentResult?.paymentId) {
        setIsProcessing(false);
        return;
      }
      paidyPaymentId = paymentResult.paymentId;

      // バックエンドで capture + apply_status 1→2 に更新
      // 新規Paidyの場合、まだpayment_method_idがないので0で送る。
      // 通信例外: backend は capture 済みかも知れないので reconcile で確認。
      // 明示失敗(success=false): authorize 残留防止のため close で補償。
      if (subscriptionId) {
        let res;
        try {
          res = await postSubscriptionInitialPaymentApi({
            t_subscription_id: subscriptionId,
            m_payment_type_id: paymentTypeId.PAIDY,
            m_payment_method_id: 0,
            paidy_payment_id: paidyPaymentId,
          });
        } catch (apiErr) {
          await reconcileAfterInitialPaymentFailure(paidyPaymentId);
          throw apiErr;
        }
        if (!res.data.success) {
          await closePaidyApi({ payment_id: paidyPaymentId }).catch(() => {});
          throw new Error(
            res.data.error_message || '初回決済の記録に失敗しました',
          );
        }
      }

      // 都度決済完了 → トークン登録ステップ表示
      setPaymentStep('initial_done');
      setIsProcessing(false);
    } catch (e: any) {
      f7.dialog.alert(e.message || 'エラーが発生しました');
      setIsProcessing(false);
    }
  };

  // ステップ2: 継続決済方法登録 — クレカ or Paidy
  const handleActivateWithCard = async (card: any) => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const methodId = card.m_payment_method_id || card.paymentMethodId;
      const activateRes = await postSubscriptionActivateApi({
        t_subscription_id: subscriptionId,
        m_payment_type_id: paymentTypeId.CREDIT,
        m_payment_method_id: methodId,
      });
      if (!activateRes.data.success) {
        throw new Error(
          activateRes.data.error_message || '有効化に失敗しました',
        );
      }
      setPaymentStep('all_done');
    } catch (e: any) {
      f7.dialog.alert(e.message || 'エラーが発生しました');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleActivateWithPaidy = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      // 既にPaidyトークンがあればそれを使う
      if (registeredPaidys.length > 0) {
        const pm = registeredPaidys[registeredPaidys.length - 1];
        const methodId = pm.m_payment_method_id || pm.paymentMethodId;
        const activateRes = await postSubscriptionActivateApi({
          t_subscription_id: subscriptionId,
          m_payment_type_id: paymentTypeId.PAIDY,
          m_payment_method_id: methodId,
        });
        if (!activateRes.data.success) {
          throw new Error(
            activateRes.data.error_message || '有効化に失敗しました',
          );
        }
        setPaymentStep('all_done');
        return;
      }

      // 新規トークン登録
      const tokenResult = await launchPaidyCheckout({ mode: 'token' });
      if (!tokenResult?.tokenId) {
        setIsProcessing(false);
        return;
      }
      const registerRes = await registerPaidyTokenApi({
        m_customer_id: authInfo.m_customer_id,
        paidy_token_id: tokenResult.tokenId,
        customer_name: '',
        customer_email: '',
        phone_number: '',
      });
      if (!registerRes.data.success) {
        throw new Error('決済情報の保存に失敗しました');
      }
      const paidyMethodId = (registerRes.data as any).data?.id;
      const activateRes = await postSubscriptionActivateApi({
        t_subscription_id: subscriptionId,
        m_payment_type_id: paymentTypeId.PAIDY,
        m_payment_method_id: paidyMethodId,
      });
      if (!activateRes.data.success) {
        throw new Error(
          activateRes.data.error_message || '有効化に失敗しました',
        );
      }
      setPaymentStep('all_done');
    } catch (e: any) {
      f7.dialog.alert(e.message || 'エラーが発生しました');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClickHome = () => {
    // subscription_t_stock_car_id は削除しない（再開時に復元するため）
    // auto-link 成否時にのみ削除する
    f7.view.main.router.navigate(paths.home);
  };

  const handleSelectCard = async (card: any) => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      f7.preloader.show();
      const { m_affiliate_id } = store.state.customerInfo as any;

      // 1. アフィリエイト設定からGMO情報取得
      const { data: settings } = await fetchMAffiliateSettingsApi({
        m_affiliate_id,
      });
      if (!settings.success) throw new Error('GMO設定の取得に失敗しました');
      const { gmopg_site_id, gmopg_site_pass, gmopg_shop_id, gmopg_shop_pass } =
        settings.data;

      const cardMethodId = card.m_payment_method_id || card.paymentMethodId;

      // 2. サブスク決済パラメータ生成
      // cardChargeParameter APIを呼ぶ（SubscriptionController経由）
      const paramRes = await import('@/config/axios').then(({ baseApi }) =>
        baseApi.post('/v1/customer/subscription/card-charge-parameter', {
          m_customer_id: authInfo.m_customer_id,
          m_subscription_plan_id:
            (subscription as any)?.m_subscription_plan_id || 1,
          m_payment_method_id: cardMethodId,
          amount: displayFee,
          ShopID: gmopg_shop_id,
          ShopPass: gmopg_shop_pass,
          siteID: gmopg_site_id,
          sitePass: gmopg_site_pass,
        }),
      );

      if (!paramRes.data.success)
        throw new Error('決済パラメータの生成に失敗しました');
      const { OrderID, MemberID } = paramRes.data.data;

      // 3. GMO決済URL生成
      const completeUrl = `${
        endpoint.BASE_URL
      }${paths.subscriptionPaymentStatus('payment_success')}`;
      const cancelUrl = `${endpoint.BASE_URL}${paths.subscriptionPaymentStatus(
        'payment_cancel',
      )}`;

      const linkRes = await postCreateGmoLinkTypePlusCardParameterApi({
        m_affiliate_id: String(m_affiliate_id),
        m_customer_id: String(authInfo.m_customer_id),
        config_id: '1',
        order_id: OrderID,
        amount: String(displayFee),
        complete_url: completeUrl,
        cancel_url: cancelUrl,
        member_id: MemberID,
      });

      if (!linkRes.data.success || !linkRes.data.data?.LinkUrl)
        throw new Error('決済URL生成に失敗しました');

      // 4. LocalStorageにリダイレクト情報を保存
      window.localStorage.setItem('token', authInfo.token);
      window.localStorage.setItem(
        'm_customer_id',
        String(authInfo.m_customer_id),
      );
      window.localStorage.setItem('fromPage', paths.subscriptionPayment);

      // GMOセッション再利用時のリダイレクト用に期待する戻り先を保存
      window.localStorage.setItem(
        'gmo_expected_return',
        paths.subscriptionPaymentStatus('payment_success'),
      );
      window.localStorage.setItem(
        'gmo_expected_cancel',
        paths.subscriptionPaymentStatus('payment_cancel'),
      );
      window.localStorage.removeItem('postGmoNav');

      // 5. GMO遷移直前にinflightフラグをセット（API失敗後にセットしない）
      const now = Date.now();
      window.localStorage.setItem(
        'subscription_payment_inflight',
        JSON.stringify({ ts: now, cid: authInfo.m_customer_id }),
      );
      setInflightTs(now);
      setIsInflight(true);

      // 6. GMO LinkPlus URLにリダイレクト（ChargePrepaidPageと同じ方式）
      const linkUrl = linkRes.data.data.LinkUrl;
      if (isApp) {
        location.href = linkUrl;
      } else {
        window.location.href = linkUrl;
      }
    } catch (e: any) {
      f7.preloader.hide();
      clearInflightStorage();
      f7.dialog.alert(e.message || 'エラーが発生しました');
      setIsProcessing(false);
    }
  };

  // 車検証登録済みかどうか
  const [autoLinkSucceeded, setAutoLinkSucceeded] = useState(false);
  const hasCarRegistered =
    autoLinkSucceeded || !!(subscription as any)?.t_stock_car_id;
  const allComplete = hasCarRegistered;

  // 既存車の auto-link: 支払い完了後に tStockCarId があれば自動紐付け
  const [autoLinkAttempted, setAutoLinkAttempted] = useState(false);
  const [autoLinkFailed, setAutoLinkFailed] = useState(false);
  useEffect(() => {
    if (
      paymentStep !== 'all_done' ||
      !tStockCarId ||
      !subscriptionId ||
      hasCarRegistered ||
      autoLinkAttempted
    )
      return;
    let cancelled = false;
    setAutoLinkAttempted(true);
    (async () => {
      try {
        const res = await postSubscriptionRegisterVehicleApi({
          t_subscription_id: subscriptionId,
          t_stock_car_id: tStockCarId,
        });
        if (cancelled) return;
        if (!res.data?.success) {
          throw new Error(
            res.data?.error_message || '車両の自動紐付けに失敗しました',
          );
        }
        // 成功確定 → autoLinkSucceeded で UI を安定させる
        // （subscription の再取得結果が stale でも hasCarRegistered は true を維持）
        setAutoLinkSucceeded(true);
        setSubscription((prev) =>
          prev ? { ...prev, t_stock_car_id: tStockCarId } : prev,
        );
        window.localStorage.removeItem('subscription_t_stock_car_id');
        setTStockCarId(undefined);
        // ステータス再取得して最新データに同期（補助）
        try {
          const statusRes = await getSubscriptionStatusApi(
            authInfo.m_customer_id,
          );
          if (
            !cancelled &&
            statusRes.data.success &&
            statusRes.data.data?.subscription
          ) {
            setSubscription(statusRes.data.data.subscription);
          }
        } catch {
          // 再取得失敗でも autoLinkSucceeded で UI は安定
        }
      } catch (e: any) {
        if (cancelled) return;
        console.error('auto registerVehicle failed', e);
        // tStockCarId は保持して再試行可能にする
        setAutoLinkFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    paymentStep,
    tStockCarId,
    subscriptionId,
    hasCarRegistered,
    autoLinkAttempted,
  ]);

  // subscriptionId 取得不能時のフォールバック:
  // fetch 完了後も subscriptionId が得られない場合は tStockCarId をクリアして手動導線を戻す
  useEffect(() => {
    if (
      paymentStep !== 'all_done' ||
      !tStockCarId ||
      subscriptionId ||
      !statusFetched
    )
      return;
    window.localStorage.removeItem('subscription_t_stock_car_id');
    setTStockCarId(undefined);
  }, [paymentStep, tStockCarId, subscriptionId, statusFetched]);

  // ステップ3: 決済完了
  if (paymentStep === 'all_done') {
    return (
      <PageTemplate
        pageName="subscription-payment"
        title={displayPlanName}
        showNavBack
        handleClickBack={handleClickHome}
      >
        <div style={{ padding: '32px 16px', textAlign: 'center' }}>
          <div
            style={{
              display: 'inline-block',
              padding: '6px 16px',
              backgroundColor: '#E3F2FD',
              borderRadius: '20px',
              fontSize: '13px',
              color: '#1E55E6',
              fontWeight: 'bold',
              marginBottom: '16px',
            }}
          >
            {allComplete ? 'サービス開始！' : 'お支払い完了！'}
          </div>

          <div
            style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 16px',
              borderRadius: '50%',
              backgroundColor: '#E8F5E9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              color: '#4CAF50',
            }}
          >
            ✓
          </div>

          <h2
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '8px',
            }}
          >
            お支払いが完了しました！
          </h2>
          {!allComplete && !isExistingCarFlow && (
            <p
              style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}
            >
              サービス開始には車検証の登録が必要です
            </p>
          )}

          <div
            style={{
              padding: '20px',
              margin: '0 0 24px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: '1px solid #e0e0e0',
              textAlign: 'left',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
              <span style={{ fontSize: '14px' }}>お申し込み</span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: '12px',
                  color: '#4CAF50',
                  fontWeight: 'bold',
                }}
              >
                完了
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
              <span style={{ fontSize: '14px' }}>初回お支払い</span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: '12px',
                  color: '#4CAF50',
                  fontWeight: 'bold',
                }}
              >
                完了
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
              <span style={{ fontSize: '14px' }}>
                来月以降のお支払い方法登録
              </span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: '12px',
                  color: '#4CAF50',
                  fontWeight: 'bold',
                }}
              >
                完了
              </span>
            </div>
            {/* 車両の本登録: 既存車フローではapply時に紐付け済みのため非表示 */}
            {!isExistingCarFlow && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 0',
                }}
              >
                {hasCarRegistered ? (
                  <>
                    <span style={{ color: '#4CAF50', fontSize: '16px' }}>
                      ✓
                    </span>
                    <span style={{ fontSize: '14px' }}>車両の本登録</span>
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontSize: '12px',
                        color: '#4CAF50',
                        fontWeight: 'bold',
                      }}
                    >
                      完了
                    </span>
                  </>
                ) : (
                  <>
                    <span style={{ color: '#FF9800', fontSize: '16px' }}>
                      ○
                    </span>
                    <span style={{ fontSize: '14px' }}>車両の本登録</span>
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontSize: '11px',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        backgroundColor: '#FFF3E0',
                        color: '#E65100',
                      }}
                    >
                      未完了
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {!hasCarRegistered && autoLinkFailed && (
            <>
              <p
                style={{
                  fontSize: '13px',
                  color: '#E65100',
                  backgroundColor: '#FFF3E0',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '12px',
                }}
              >
                車両の自動登録に失敗しました
              </p>
              <button
                onClick={() => {
                  setAutoLinkFailed(false);
                  setAutoLinkAttempted(false);
                }}
                style={{
                  width: '100%',
                  padding: '16px',
                  marginBottom: '12px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#fff',
                  backgroundColor: '#1E55E6',
                  border: 'none',
                  borderRadius: '28px',
                  cursor: 'pointer',
                }}
              >
                再試行
              </button>
              <button
                onClick={() => {
                  window.localStorage.removeItem('subscription_t_stock_car_id');
                  setTStockCarId(undefined);
                  setAutoLinkFailed(false);
                  const affiliateId = (store.state as any).customerInfo
                    ?.m_affiliate_id;
                  storeDispatch('setRegisterUser', 'registerUser', {
                    ...initialState.registerUser,
                    affiliate_id: Number(affiliateId),
                    currentStep: 3,
                  });
                  f7.view.main.router.navigate(paths.registerNewMember, {
                    props: {
                      sourcePage: paths.subscriptionComplete,
                      startStep: 3,
                    },
                  });
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginBottom: '12px',
                  fontSize: '14px',
                  color: '#1E55E6',
                  backgroundColor: '#fff',
                  border: '1px solid #1E55E6',
                  borderRadius: '28px',
                  cursor: 'pointer',
                }}
              >
                車検証を登録する
              </button>
            </>
          )}

          {!hasCarRegistered && !tStockCarId && !autoLinkFailed && (
            <button
              onClick={() => {
                const affiliateId = (store.state as any).customerInfo
                  ?.m_affiliate_id;
                storeDispatch('setRegisterUser', 'registerUser', {
                  ...initialState.registerUser,
                  affiliate_id: Number(affiliateId),
                  currentStep: 3,
                });
                f7.view.main.router.navigate(paths.registerNewMember, {
                  props: {
                    sourcePage: paths.subscriptionComplete,
                    startStep: 3,
                  },
                });
              }}
              style={{
                width: '100%',
                padding: '16px',
                marginBottom: '12px',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#fff',
                backgroundColor: '#1E55E6',
                border: 'none',
                borderRadius: '28px',
                cursor: 'pointer',
              }}
            >
              車検証を登録する
            </button>
          )}

          <button
            onClick={handleClickHome}
            style={{
              width: '100%',
              padding: '16px',
              fontSize: '14px',
              color: allComplete || isExistingCarFlow ? '#fff' : '#666',
              backgroundColor:
                allComplete || isExistingCarFlow ? '#1E55E6' : '#fff',
              border:
                allComplete || isExistingCarFlow ? 'none' : '1px solid #ccc',
              borderRadius: '28px',
              cursor: 'pointer',
            }}
          >
            {allComplete || isExistingCarFlow
              ? 'ホームに戻る'
              : 'あとで登録する'}
          </button>
        </div>
      </PageTemplate>
    );
  }

  // ステップ2: 初回決済完了 → トークン登録の説明
  if (paymentStep === 'initial_done') {
    return (
      <PageTemplate pageName="subscription-payment" title="お支払い情報の登録">
        <div style={{ padding: '32px 16px', textAlign: 'center' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 16px',
              borderRadius: '50%',
              backgroundColor: '#E8F5E9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              color: '#4CAF50',
            }}
          >
            ✓
          </div>

          <h2
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '8px',
            }}
          >
            初回のお支払いが完了しました
          </h2>

          <p
            style={{
              fontSize: '14px',
              color: '#666',
              marginBottom: '24px',
              lineHeight: 1.6,
            }}
          >
            続けて、毎月の自動引き落としのために
            <br />
            お支払い方法の登録をお願いします。
          </p>

          {/* ステータス */}
          <div
            style={{
              padding: '20px',
              margin: '0 0 24px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: '1px solid #e0e0e0',
              textAlign: 'left',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
              <span style={{ fontSize: '14px' }}>お申し込み</span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: '12px',
                  color: '#4CAF50',
                  fontWeight: 'bold',
                }}
              >
                完了
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
              <span style={{ fontSize: '14px' }}>初回お支払い</span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: '12px',
                  color: '#4CAF50',
                  fontWeight: 'bold',
                }}
              >
                完了
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <span style={{ color: '#FF9800', fontSize: '16px' }}>○</span>
              <span style={{ fontSize: '14px' }}>
                来月以降のお支払い方法登録
              </span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: '11px',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  backgroundColor: '#FFF3E0',
                  color: '#E65100',
                }}
              >
                未完了
              </span>
            </div>
            {/* 車両の本登録: 既存車フローではapply時に紐付け済みのため非表示 */}
            {!isExistingCarFlow && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 0',
                }}
              >
                {hasCarRegistered ? (
                  <>
                    <span style={{ color: '#4CAF50', fontSize: '16px' }}>
                      ✓
                    </span>
                    <span style={{ fontSize: '14px' }}>車両の本登録</span>
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontSize: '12px',
                        color: '#4CAF50',
                        fontWeight: 'bold',
                      }}
                    >
                      完了
                    </span>
                  </>
                ) : (
                  <>
                    <span style={{ color: '#FF9800', fontSize: '16px' }}>
                      ○
                    </span>
                    <span style={{ fontSize: '14px' }}>車両の本登録</span>
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontSize: '11px',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        backgroundColor: '#FFF3E0',
                        color: '#E65100',
                      }}
                    >
                      未完了
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* 継続決済方法の選択 */}
          <div
            style={{
              fontSize: '14px',
              fontWeight: 'bold',
              textAlign: 'left',
              marginBottom: '12px',
            }}
          >
            お支払い方法を選択
          </div>

          {registeredCards.map((card: any) => (
            <div
              key={card.m_payment_method_id || card.paymentMethodId}
              onClick={() => handleActivateWithCard(card)}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px',
                marginBottom: '8px',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                cursor: isProcessing ? 'wait' : 'pointer',
                opacity: isProcessing ? 0.6 : 1,
              }}
            >
              <i
                className="icon-credit-card"
                style={{ fontSize: '18px', marginRight: '12px', color: '#333' }}
              />
              <div style={{ flex: 1, fontSize: '14px' }}>
                クレジットカード（{card.cardNo || card.card_no}）
              </div>
              <i className="icon-right" style={{ color: '#bbb' }} />
            </div>
          ))}

          <div
            onClick={handleActivateWithPaidy}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '16px',
              marginBottom: '8px',
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              cursor: isProcessing ? 'wait' : 'pointer',
              opacity: isProcessing ? 0.6 : 1,
            }}
          >
            <img
              src={PAIDY_LOGO_URL}
              alt="ペイディ"
              style={{ width: '50px', marginRight: '12px' }}
            />
            <div style={{ flex: 1, fontSize: '14px' }}>
              あと払い（ペイディ）
            </div>
            <i className="icon-right" style={{ color: '#bbb' }} />
          </div>

          <button
            onClick={async () => {
              try {
                const returnUrl =
                  paths.subscriptionPaymentStatus('gmo_success');
                const cancelUrl = paths.subscriptionPaymentStatus('gmo_cancel');
                window.localStorage.setItem(
                  'destination_page',
                  paths.subscriptionPayment,
                );
                const response = await addCreditCardPaymentMethod(
                  returnUrl,
                  cancelUrl,
                );
                if (
                  !response ||
                  !response.success ||
                  !('LinkUrl' in response.data)
                ) {
                  f7.dialog.alert('カード登録の準備に失敗しました');
                  return;
                }
                const linkUrl = response.data.LinkUrl;
                if (isApp) {
                  location.href = linkUrl;
                } else {
                  window.location.href = linkUrl;
                }
              } catch (error: any) {
                f7.dialog.alert(
                  error?.message || 'カード登録の処理中にエラーが発生しました',
                );
              }
            }}
            style={{
              width: '100%',
              padding: '12px',
              marginTop: '8px',
              fontSize: '13px',
              color: '#1E55E6',
              backgroundColor: '#fff',
              border: '1px solid #1E55E6',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            新しいクレジットカードを登録
          </button>

          {!hasCarRegistered && !tStockCarId && (
            <button
              onClick={() => {
                const affiliateId = (store.state as any).customerInfo
                  ?.m_affiliate_id;
                storeDispatch('setRegisterUser', 'registerUser', {
                  ...initialState.registerUser,
                  affiliate_id: Number(affiliateId),
                  currentStep: 3,
                });
                f7.view.main.router.navigate(paths.registerNewMember, {
                  props: {
                    sourcePage: paths.subscriptionPayment,
                    startStep: 3,
                  },
                });
              }}
              style={{
                width: '100%',
                padding: '16px',
                marginTop: '12px',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#1E55E6',
                backgroundColor: '#fff',
                border: '1px solid #1E55E6',
                borderRadius: '28px',
                cursor: 'pointer',
              }}
            >
              車検証を登録する
            </button>
          )}
        </div>
      </PageTemplate>
    );
  }

  // GMOセッション再利用リダイレクト中は描画しない
  if (isGmoRedirecting) return null;

  // ローディング表示条件（二重決済防止）:
  // ローディング表示: inflight中かつStep1のまま（二重決済防止）
  // payment_success到着時にinflightが無ければGMO戻りuseEffectでセット済み
  if (isInflight && paymentStep === 'select') {
    return (
      <PageTemplate pageName="subscription-payment" title="お支払い処理中">
        <div style={{ padding: '32px 16px', textAlign: 'center' }}>
          <p style={{ fontSize: '14px', color: '#666' }}>
            決済状態を確認しています...
          </p>
        </div>
      </PageTemplate>
    );
  }

  // ステップ1: 決済方法選択
  return (
    <>
      <PageTemplate
        pageName="subscription-payment"
        title="お支払い情報の入力"
        showNavBack
        handleClickBack={handleClickHome}
      >
        <div style={{ padding: '16px' }}>
          <div
            style={{
              padding: '16px',
              marginBottom: '16px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: '1px solid #e0e0e0',
            }}
          >
            <div
              style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}
            >
              初回お支払い金額
            </div>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
              ¥{displayFee.toLocaleString()}
            </div>
            <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
              以降、毎月 ¥{displayFee.toLocaleString()}{' '}
              が自動的にお支払いされます
            </div>
          </div>

          <p
            style={{
              fontSize: '14px',
              color: '#666',
              marginBottom: '16px',
              lineHeight: 1.6,
            }}
          >
            お支払い方法を選択してください。
          </p>

          {registeredCards.map((card: any) => (
            <div
              key={card.paymentMethodId}
              onClick={() => handleSelectCard(card)}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px',
                marginBottom: '8px',
                backgroundColor: '#fff',
                borderRadius: '12px',
                border: '1px solid #e0e0e0',
                cursor: 'pointer',
              }}
            >
              <i
                className="icon-credit-card"
                style={{ fontSize: '20px', marginRight: '12px', color: '#333' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '15px', fontWeight: 'bold' }}>
                  クレジットカード
                </div>
                <div
                  style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}
                >
                  {card.cardNo}
                </div>
              </div>
              <div style={{ color: '#ccc', fontSize: '20px' }}>›</div>
            </div>
          ))}

          {/* 新規クレジットカード登録 */}
          <div
            onClick={async () => {
              try {
                const returnUrl =
                  paths.subscriptionPaymentStatus('gmo_success');
                const cancelUrl = paths.subscriptionPaymentStatus('gmo_cancel');
                window.localStorage.setItem(
                  'destination_page',
                  paths.subscriptionPayment,
                );
                const response = await addCreditCardPaymentMethod(
                  returnUrl,
                  cancelUrl,
                );
                if (
                  !response ||
                  !response.success ||
                  !('LinkUrl' in response.data)
                ) {
                  f7.dialog.alert('カード登録の準備に失敗しました');
                  return;
                }
                const linkUrl = response.data.LinkUrl;
                if (isApp) {
                  location.href = linkUrl;
                } else {
                  window.location.href = linkUrl;
                }
              } catch (error: any) {
                console.error('GMOカード登録エラー:', error);
                f7.dialog.alert(
                  error?.message || 'カード登録の処理中にエラーが発生しました',
                );
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '16px',
              marginBottom: '8px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: '1px dashed #e0e0e0',
              cursor: 'pointer',
            }}
          >
            <i
              className="icon-credit-card"
              style={{
                fontSize: '20px',
                marginRight: '12px',
                color: '#1E55E6',
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', color: '#1E55E6' }}>
                新しいクレジットカードを登録
              </div>
            </div>
            <div style={{ color: '#ccc', fontSize: '20px' }}>›</div>
          </div>

          {/* Paidy（都度決済 → 登録済みならStep2スキップ） */}
          <div
            onClick={() =>
              registeredPaidys.length > 0
                ? handleRegisteredPaidy(registeredPaidys[0])
                : handleSelectPaidy()
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '16px',
              marginBottom: '8px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: '1px solid #e0e0e0',
              cursor: isProcessing ? 'wait' : 'pointer',
              opacity: isProcessing ? 0.6 : 1,
            }}
          >
            <img
              src={PAIDY_LOGO_URL}
              alt="ペイディ"
              style={{ width: '57px', marginRight: '12px' }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '15px', fontWeight: 'bold' }}>
                あと払い（ペイディ）
              </div>
              <div
                style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}
              >
                メールアドレスと電話番号で簡単登録
              </div>
            </div>
            <div style={{ color: '#ccc', fontSize: '20px' }}>›</div>
          </div>
        </div>
      </PageTemplate>
    </>
  );
};
